/**
 * La file d'attente commune par spécialité (RG-PA-10, spécification S21).
 *
 * Un box est une salle de consultation d'une agence — médecine générale,
 * dentiste, ophtalmologie. Les box d'un site suivent sa dotation : un par
 * médecin prévu, de sa prestation. Un box est **ouvert** quand un praticien
 * le tient.
 *
 * L'accueil dit la prestation ; le patient attend dans la file de cette
 * prestation, sans box. Le box se fixe à l'appel : le premier praticien libre
 * prend le plus ancien des patients qu'il peut recevoir.
 */
import { z } from 'zod';
import { PRIORITES_VISITE, REPOS_MEDICAL_MAX_JOURS, schemaVisite } from './visites.js';
import { schemaPaiementAuDentiste } from './prix-dentiste.js';
const praticien = z.object({
    id: z.uuid(),
    nom: z.string(),
    prenom: z.string().nullable(),
});
export const schemaBox = z.object({
    id: z.uuid(),
    siteId: z.uuid(),
    nom: z.string(),
    /**
     * Le numéro de sa porte (« Porte 1 ») : ce que lit la salle d'attente.
     * `null` tant que la Direction ne l'a pas donné — le box reste alors hors file.
     */
    porte: z.string().nullable(),
    /** La prestation qu'on y reçoit, dans les mots du registre. */
    prestation: z.string(),
    actif: z.boolean(),
    ordre: z.number().int(),
    /** Un praticien le tient : il appelle les patients de cette prestation. */
    ouvert: z.boolean(),
    praticien: praticien.nullable(),
    ouvertLe: z.iso.datetime().nullable(),
    enConsultation: z.number().int(),
});
export const schemaFiltresBoxes = z.object({
    /** Le site du poste par défaut ; un rôle transverse le précise. */
    siteId: z.uuid().optional(),
    /** Seulement ceux qu'un praticien tient — ce que l'accueil propose. */
    ouverts: z.coerce.boolean().optional(),
});
/**
 * Donner à un box le numéro de sa porte (« Porte 1 »). C'est ce que la salle
 * d'attente lit : il ne dit jamais la spécialité. La prestation et
 * l'existence du box suivent la dotation du site : elles se changent là.
 */
export const schemaModificationBox = z
    .object({ porte: z.string().trim().min(1, 'Numéro de porte requis.').max(30) })
    .strict();
/** Un patient qui attend, avec ce que la file commune sait de lui. */
export const schemaVisiteEnFile = schemaVisite.extend({
    /** R5, R7 — l'étiquette « praticien demandé », et s'il tient encore un box de cette prestation. */
    praticienDemande: praticien.extend({ tientUnBox: z.boolean() }).nullable(),
    /** R8 et R9 : les appels restés sans réponse. */
    appelsSansReponse: z.number().int(),
    /** R8 : personne ne l'appelle avant cet instant. */
    misDeCoteJusquA: z.iso.datetime().nullable(),
});
/**
 * Décision 279 : chez le médecin, le métier du patient (`Personne.fonction`,
 * `null` pour un ayant droit ou un externe). Ma file seulement : la file du
 * site, que lit l'accueil, ne le porte pas.
 */
const metier = { fonction: z.string().nullable() };
export const schemaVisiteDuPraticien = schemaVisite.extend({
    ...metier,
    /**
     * AJ-02 — le patient en consultation est non couvert et paie au dentiste :
     * le prix à saisir avant de terminer. Absent ou `null` : rien à saisir.
     */
    paiementAuDentiste: schemaPaiementAuDentiste.nullable().optional(),
});
export const schemaVisiteEnFileDuPraticien = schemaVisiteEnFile.extend(metier);
/** La file d'un site : une file par prestation, dans l'ordre d'arrivée, puis les box. */
export const schemaFileAttente = z.object({
    siteId: z.uuid(),
    files: z.array(z.object({ prestation: z.string(), attente: z.array(schemaVisiteEnFile) })),
    boxes: z.array(schemaBox.extend({ enCours: schemaVisite.nullable() })),
    /** Le paramètre `file.praticien_demande` : sans lui, ni champ ni étiquette à l'écran. */
    praticienDemandeActif: z.boolean(),
    /** R10, R16 — retirés aujourd'hui : l'accueil peut les remettre dans la file. */
    retires: z.array(schemaVisiteEnFile),
});
/** Le poste du praticien : son box, son patient, la file de sa spécialité — ou les box à prendre. */
export const schemaMaFile = z.object({
    box: schemaBox.nullable(),
    /** Les box actifs de son site que personne ne tient. */
    boxesLibres: z.array(schemaBox),
    enCours: schemaVisiteDuPraticien.nullable(),
    /** Les patients qu'il peut recevoir, dans l'ordre d'arrivée — mis de côté compris. */
    fileDeMaSpecialite: z.array(schemaVisiteEnFileDuPraticien),
    /** Parmi eux, ceux que « suivant » saute : mis de côté (R8) ou sans réponse deux fois (R9). */
    misDeCote: z.number().int(),
    /**
     * Le patient que « suivant » donnerait maintenant, selon l'horloge du
     * serveur : l'écran ne le recalcule pas (R17, prise sans motif).
     */
    prochainId: z.uuid().nullable(),
    /**
     * L'heure du serveur à la réponse : l'écran en déduit l'écart avec sa propre
     * horloge, pour qu'une tablette mal réglée ne fausse pas les durées affichées.
     */
    maintenant: z.iso.datetime(),
});
/** Une journée de praticien tient largement sous ce plafond : il borne la réponse. */
export const PATIENTS_RECUS_MAX = 200;
/**
 * Un patient que le praticien connecté a reçu aujourd'hui (G19) : de quoi le
 * retrouver et voir le rythme de la journée. Rien de médical (CCT § 5.2).
 */
export const schemaPatientRecu = z.object({
    visiteId: z.uuid(),
    personne: z.object({ id: z.uuid(), nom: z.string(), prenom: z.string().nullable() }),
    prestation: z.string(),
    priseEnChargeLe: z.iso.datetime().nullable(),
    /** AJ-07 — le début de la consultation ; vide avant AJ-07, la durée part alors de l'appel. */
    consultationCommenceeLe: z.iso.datetime().nullable(),
    termineeLe: z.iso.datetime(),
});
/** R6 — changer ou retirer (`null`) l'étiquette d'un patient qui attend. */
export const schemaPraticienDemande = z.object({ praticienId: z.uuid().nullable() }).strict();
/**
 * R17 — prendre ce patient, au lieu de « suivant ». Le motif est exigé quand
 * ce n'est pas celui que « suivant » aurait donné ; il reste sur la visite,
 * jamais au journal.
 */
export const schemaPriseDirecte = z
    .object({
    visiteId: z.uuid(),
    motif: z.string().trim().min(3, 'Trois caractères au moins.').max(200).optional(),
})
    .strict();
/**
 * AJ-07 — pourquoi le praticien remet dans la file un patient dont la
 * consultation a commencé (R11). Une liste fermée : le journal note le code,
 * jamais de donnée médicale. Libellés ajustables par le TL.
 */
export const MOTIFS_REMISE_EN_FILE = [
    'EXAMEN_A_FAIRE',
    'AVIS_AUTRE_PRATICIEN',
    'URGENCE_AVANT',
    'REVIENT_PLUS_TARD',
    'AUTRE',
];
export const LIBELLES_MOTIF_REMISE_EN_FILE = {
    EXAMEN_A_FAIRE: 'Examen ou analyse à faire',
    AVIS_AUTRE_PRATICIEN: 'Avis d’un autre praticien',
    URGENCE_AVANT: 'Urgence à prendre avant',
    REVIENT_PLUS_TARD: 'Le patient revient plus tard',
    AUTRE: 'Autre',
};
/** « Autre » : quelques mots, sans donnée médicale. */
export const PRECISION_REMISE_MIN = 5;
export const PRECISION_REMISE_MAX = 120;
/**
 * Le corps de « Remettre dans la file » : vide pour un patient seulement
 * appelé ; le motif est exigé par le serveur une fois la consultation commencée.
 */
export const schemaRemiseEnFile = z
    .object({
    motif: z.enum(MOTIFS_REMISE_EN_FILE).optional(),
    precision: z
        .string()
        .trim()
        .min(PRECISION_REMISE_MIN, `${String(PRECISION_REMISE_MIN)} caractères au moins.`)
        .max(PRECISION_REMISE_MAX)
        .optional(),
})
    .strict()
    .refine((v) => v.motif !== 'AUTRE' || v.precision !== undefined, {
    message: 'Précisez le motif « Autre ».',
    path: ['precision'],
})
    .refine((v) => v.precision === undefined || v.motif === 'AUTRE', {
    message: 'Une précision ne va qu’avec le motif « Autre ».',
    path: ['precision'],
});
/**
 * La fin de consultation : c'est ici que se remplit la colonne « repos médical » du cahier.
 * Le repos est exigé par le service (décision 446), pas par ce schéma : un corps
 * vide reçoit ainsi un 422 qui dit quoi faire, au lieu d'un refus de validation.
 */
export const schemaFinConsultation = z
    .object({
    reposMedicalJours: z.number().int().min(0).max(REPOS_MEDICAL_MAX_JOURS).optional().nullable(),
    assistanceMaternelle: z.boolean().optional(),
    observation: z.string().trim().max(300).optional().nullable(),
})
    .strict();
/** RG-PA-10 — la priorité, posée par le corps médical, toujours motivée. */
export const schemaPrioriteVisite = z
    .object({
    priorite: z.enum(PRIORITES_VISITE),
    motifPriorite: z.string().trim().max(200).optional().nullable(),
})
    .strict();
//# sourceMappingURL=file-attente.js.map